import { USER_TYPE } from 'constants/UserConstants';
import { createReducer } from 'utils/ReduxUtils';

import * as actionTypes from '../../actionTypes';

/**
 * @typedef {{
 *   createdById?: string,
 *   customerId?: string,
 *   isCustomer: boolean,
 *   isScheduler: boolean,
 *   isUser: boolean,
 * }} StoreMessageAttributes
 */

/**
 * @typedef {StoreMessageAttributes & Omit<import("modules/chat/types/projections/paginated-message").PaginatedMessage, 'createBy' | 'customer'>} StoredMessage
 */

/**
 * @typedef {Record<string, StoredMessage>} ChatDataMessageByIdReducer
 */

/** @type {ChatDataMessageByIdReducer} */
const initialState = {};

/**
 * @param {StoredMessage | import("modules/chat/types/projections/paginated-message").PaginatedMessage} message
 * @returns {StoredMessage}
 */
const messageCleaner = message => {
  const { createdBy, createdById, customerId, reactions = [], ...newMessage } = message;
  const { customer } = newMessage;

  return {
    ...newMessage,
    reactions,
    ...(createdById || createdBy?.id ? { createdById: createdById ?? createdBy?.id } : {}),
    ...(customerId || customer?.id ? { customerId: customerId ?? customer?.id } : {}),
    isCustomer: createdBy?.type === USER_TYPE.SYSTEM && (createdBy?.onCustomerBehalf ?? false),
    isScheduler: createdBy?.type === USER_TYPE.SYSTEM && (createdBy?.scheduler ?? false),
    isUser: createdBy?.type === USER_TYPE.USER,
  };
};

const updateAll = (state, { payload }) => ({
  ...state,
  ...(payload.content
    ?.filter(m => !!m)
    .reduce((messages, message) => ({ ...messages, [message.id]: messageCleaner(message) }), {}) ?? {}),
});

const updateOne = (state, { payload }) => ({
  ...state,
  [payload.id]: {
    ...(state?.[payload.id] ?? {}),
    ...messageCleaner(payload),
  },
});

const afterReceiveMessage = (state, { payload: { message } }) => updateOne(state, { payload: message });

const updateAllFromConversations = (state, { payload }) => {
  const content = payload.content.map(({ latestMessage }) => latestMessage);
  return updateAll(state, { payload: { content } });
};

const updateOneFromConversation = (state, { payload }) => {
  const { latestMessage } = payload;
  if (!latestMessage) {
    return state;
  }
  return updateOne(state, { payload: latestMessage });
};

const afterPostMessage = (state, action) => {
  const payload = {
    ...action.payload,
    createdBy: {
      ...(action.payload.createdBy ?? {}),
      type: USER_TYPE.USER,
    },
  };

  return updateOne(state, { ...action, payload });
};

export default createReducer(initialState, {
  [actionTypes.FETCH_MESSAGES_SUCCESS]: updateAll,
  [actionTypes.POST_MESSAGE_SUCCESS]: afterPostMessage,
  [actionTypes.RECEIVE_MESSAGE_FROM_SOCKET]: afterReceiveMessage,
  [actionTypes.FETCH_CONVERSATION_SUCCESS]: updateOneFromConversation,
  [actionTypes.FETCH_CONVERSATIONS_SUCCESS]: updateAllFromConversations,
});
